import { subDays, subMonths } from 'date-fns';
import { union } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    AsyncSelectInput,
    autoinject,
    bind,
    componentReaction,
    FormStore,
    InfiniteScroll,
    inject,
    NothingHere,
    SubTopBar,
    Title,
    toArray,
    uuidv4generate,
    getValueFromOption,
    RetryPolicy,
    DateRangePicker,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import Select, { ValueType } from 'react-select';
import { Form } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { WarrantyAlertsStore } from './WarrantyAlertsStore';
import { WarrantyAlertLogItem } from './WarrantyAlertLogItem';
import { DisplaysStore } from '../Displays/DisplaysStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AlertActionsStore } from '../AlertActions/AlertActionsStore';
import { DisplayEventProfileStore } from '../Alerts/DisplayEventProfileStore';
import { AccountStore } from '../Account/AccountStore';
import { BsFillExclamationSquareFill } from '@react-icons/all-files/bs/BsFillExclamationSquareFill';

interface IOption {
    label: string;
    value: string;
}

export interface IWarrantyAlertLogsProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class WarrantyAlertLogsList extends React.Component<IWarrantyAlertLogsProps> {
    public constructor(props: IWarrantyAlertLogsProps) {
        super(props);
        const trackedValues = computed(() => [
            this.dateFormStore.fields.startTime.value,
            this.dateFormStore.fields.endTime.value,
        ]);
        componentReaction(this, () => trackedValues.get(), this.onFilterChange);
    }

    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            this.loadMoreAsync();
            await this.displayEventProfileStore.loadAllAsync();
            await this.accountsStore.loadAsync();
            await this.alertActionsStore.loadRecipientsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid">
                <Title>Warranty Alerts</Title>

                <SubTopBar>
                    {/*<Input*/}
                    {/*    type="text"*/}
                    {/*    placeholder="Search Alert Logs..."*/}
                    {/*    value={this.searchValue}*/}
                    {/*    onChange={this.onSearchInputChange}*/}
                    {/*/>*/}

                    <div className="row w-100 g-2 p-static">
                        {this.accountStore.isInRoles(['MriAdmin', 'MriUser']) && (
                            <div className="col-md-6 col-lg-3">
                                <Select
                                    options={this.accountOptions}
                                    classNamePrefix="react-select"
                                    placeholder="Accounts..."
                                    onChange={this.selectAccount}
                                    isClearable={true}
                                    className="minw-200 position-static alert-list-select"
                                />
                            </div>
                        )}
                        <div className="col-md-6 col-lg-3">
                            <AsyncSelectInput
                                className="w-100 react-select-container d-inline-block position-static alert-list-select"
                                placeholder="Serial Numbers..."
                                loadOptions={this.loadSerialNumberOptionsAsync}
                                getOptionLabel={x => x!}
                                getOptionValue={x => x!}
                                defaultOptions
                                isMulti
                                onChange={() => this.onSerialInputChange()}
                                field={this.displayAlertFilterForm.fields.serialNumbers}
                            />
                        </div>
                        {this.accountStore.isInRoles(['MriAdmin', 'MriUser']) && (
                            <div className="col-md-6 col-lg-3">
                                <Select
                                    options={this.profileOptions}
                                    classNamePrefix="react-select"
                                    placeholder="Profiles..."
                                    onChange={this.selectProfile}
                                    isClearable={true}
                                    className="minw-200 position-static alert-list-select"
                                />
                            </div>
                        )}

                        {this.accountStore.isInRoles(['MriAdmin', 'MriUser']) && (
                            <div className="col-md-6 col-lg-3">
                                <Select
                                    classNamePrefix="react-select"
                                    options={this.usersOptions}
                                    onChange={this.selectUser}
                                    placeholder="Emails..."
                                    className="position-static alert-list-select"
                                />
                            </div>
                        )}

                        {!this.accountStore.isInRoles(['MriAdmin', 'MriUser']) && (
                            <div className="col-md-6 col-lg-3">
                                <Form>
                                    <div className="d-block w-100">
                                        <DateRangePicker
                                            startDate={this.dateFormStore.fields.startTime}
                                            endDate={this.dateFormStore.fields.endTime}
                                            className="d-inline-block me-2"
                                            timeFormat="h:mm a"
                                            minDate={subMonths(new Date(), 6)}
                                            maxDate={new Date()}
                                            showTimeSelect
                                        />
                                    </div>
                                </Form>
                            </div>
                        )}

                        {this.accountStore.isInRoles(['MriAdmin', 'MriUser']) && (
                            <div className="col-12">
                                <div className="text-center w-100">
                                    <Form>
                                        <div className="d-block w-100">
                                            <DateRangePicker
                                                startDate={this.dateFormStore.fields.startTime}
                                                endDate={this.dateFormStore.fields.endTime}
                                                className="d-inline-block me-2"
                                                timeFormat="h:mm a"
                                                minDate={subMonths(new Date(), 6)}
                                                maxDate={new Date()}
                                                showTimeSelect
                                            />
                                        </div>
                                    </Form>
                                </div>
                            </div>
                        )}
                    </div>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <InfiniteScroll
                            throttle={100}
                            threshold={300}
                            hasMore={this.hasMore}
                            onLoadMore={this.loadMoreAsync}
                            container={this.appStore.mainContainer} // this.listContainer WHY THOUGH IT WON'T WORK
                        >
                            <table className="table table-striped w-100 h-100 fixed-header">
                                {this.displayAlertFilterForm && (
                                    <thead className="thead">
                                        <tr>
                                            <th scope="col" className="no-wrap">
                                                Row #
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Time
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Account
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Display
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Event/Alert Profile
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Email
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Details
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Status
                                            </th>
                                            <th scope="col" className="no-wrap">
                                                Duration
                                            </th>
                                        </tr>
                                    </thead>
                                )}

                                <tbody>
                                    {this.alertLogs.map((a, idx) => (
                                        <WarrantyAlertLogItem
                                            key={a.displayAlertGuid + a.email}
                                            da={a}
                                            rowNumber={idx + 1}
                                        />
                                    ))}
                                </tbody>
                            </table>

                            <NothingHere
                                empty={!this.alertLogs.length}
                                icon={<BsFillExclamationSquareFill />}
                                loading={this.hasMore || this.loading}
                                label="No Alerts"
                                loadingMessage="Loading Warranty Alerts..."
                            />
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async selectAccount(value: ValueType<IOption, boolean>) {
        const selectedValue = getValueFromOption(value) as string;
        const account = this.accountsStore.accounts.get(selectedValue);

        if (account) {
            this.displayAlertFilterForm!.fields.accounts.setValue([account]);
        } else {
            this.displayAlertFilterForm!.fields.accounts.setValue(null);
        }

        this.onFilterChange();
    }

    @bind
    @action
    private async selectProfile(value: ValueType<IOption, boolean>) {
        const selectedValue = getValueFromOption(value) as string;

        if (selectedValue) {
            this.displayAlertFilterForm!.fields.profiles.setValue([selectedValue]);
        } else {
            this.displayAlertFilterForm!.fields.profiles.setValue(null);
        }

        this.onFilterChange();
    }

    @bind
    @action
    private async selectUser(value: ValueType<IOption, boolean>) {
        const selectedValue = getValueFromOption(value) as string;

        if (selectedValue) {
            this.displayAlertFilterForm!.fields.emails.setValue([selectedValue]);
        } else {
            this.displayAlertFilterForm!.fields.emails.setValue(null);
        }

        this.onFilterChange();
    }

    @bind
    @action
    private async onSerialInputChange() {
        if (this.displayAlertFilterForm?.fields.serialNumbers.value?.length == 0) {
            this.displayAlertFilterForm?.fields.serialNumbers.setValue(null);
        }

        this.onFilterChange();
    }

    @bind
    @action
    private async onFilterChange() {
        this.alertLogGuids = [];
        this.loadMoreAsync();
    }

    @bind
    private async loadSerialNumberOptionsAsync(value: string) {
        const search: Mri.Web.Portal.Controllers.Displays.SearchModel = {
            displayFilter: {
                serialNumberSearch: value,
            },
            limit: 10,
            offset: 0,
            orderBy: '',
            searchGuid: uuidv4generate(),
            aggregates: false,
            status: false,
            telemetry: false,
            where: '',
        };
        const response = await this.displaysStore.loadWhereSearchAsync(search);
        if (response) {
            return response.result;
        } else {
            return [];
        }
    }

    @bind
    @action
    private async loadMoreAsync(limit?: number) {
        this.loading = true;

        if (this.displayAlertFilterForm) {
            this.displayAlertFilterForm.fields.startTime.setValue(this.dateFormStore.fields.startTime.value);
            this.displayAlertFilterForm.fields.endTime.setValue(this.dateFormStore.fields.endTime.value);

            try {
                this.scrollCount += this.limit;

                const search: Mri.Web.Portal.Controllers.DisplayAlerts.AlertLogSearchModel = {
                    displayAlertFilter: {
                        ...this.displayAlertFilterForm!.values,
                    },
                    limit: limit ?? this.limit,
                    offset: this.alertLogGuids.length,
                    orderBy: this.sort.join(','),
                    searchGuid: this.searchGuid,
                    where: '',
                };

                const response = await this.retryPolicy.execute(() =>
                    this.warrantyAlertsStore.loadWhereSearchAsync(search)
                );
                if (response) {
                    this.total = response.total;
                    this.alertLogGuids = union(this.alertLogGuids, response.result);
                }
            } catch (e: any) {
                if (e.name != 'AbortError') {
                    console.error(e);
                }
            }
        }

        this.loading = false;
    }

    @computed
    private get accountOptions() {
        const accounts = toArray(this.accountsStore.accounts).map(a => {
            return {
                value: a.accountNumber,
                label: a.accountName,
            };
        });

        return accounts;
    }

    @computed
    private get profileOptions() {
        return toArray(this.displayEventProfileStore.displayEventProfiles).map(r => {
            return {
                value: r.displayEventProfileGuid,
                label: r.name,
            };
        });
    }

    @computed
    private get usersOptions() {
        return toArray(this.alertActionsStore.recipients).map(r => {
            return {
                value: r.id,
                label: r.email,
            };
        });
    }

    @computed
    private get hasMore() {
        return this.alertLogGuids.length != this.total || this.scrollCount < this.alertLogGuids.length;
    }

    @computed
    private get alertLogs() {
        return this.alertLogGuids
            .map(a => this.warrantyAlertsStore.alertLogs.get(a))
            .filter(a => !!a) as Mri.DisplayAlerts.DisplayAlertLog[];
    }

    @observable
    private alertLogGuids: string[] = [];
    @observable
    private scrollCount = 40;
    @observable
    private loading = false;
    @observable
    private displayAlertFilterForm = new FormStore<Mri.DisplayAlerts.DisplayAlertFilter>({
        accounts: null,
        serialNumbers: null,
        emails: null,
        profiles: null,
        startTime: subDays(new Date(), 7),
        endTime: new Date(),
    });
    private dateFormStore = new FormStore({
        startTime: subDays(new Date(), 7),
        endTime: new Date(),
    });
    @observable
    private total: number = 0;
    @observable
    private sort: string[] = [];
    @observable
    private offset: number = 0;
    private limit: number = 40;
    private searchGuid = uuidv4generate();
    private readonly retryPolicy = new RetryPolicy<Mri.WebApi.PagingResult<string> | undefined>(
        2,
        () => 3000,
        undefined,
        e => e.exceptionType == 'Overloaded'
    );

    @inject
    private accountsStore!: AccountsStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private warrantyAlertsStore!: WarrantyAlertsStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
    @inject
    private displayEventProfileStore!: DisplayEventProfileStore;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
}
